import { useEffect } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Stack,
  Button,
  ControllerAlert,
  ControllerModal,
  ControllerValidatedPasswordField,
  ControllerValidatedSelect,
  ControllerValidatedTextField,
  DialogActions,
  DialogContent,
  MenuItem,
  Typography,
  useForm,
  yupResolver,
  Link,
} from '@bymason-private/legacy-pattern-components';
import {
  ProvisionQRCodeModel,
  useCreateProvisionSession,
  useCreateScopedSession,
  useGetGroups,
} from '@bymason/controller-api-hooks';
import { useToast } from '../../../';
import { TimeZoneSelect } from './time-zone-select/TimeZoneSelect';
import { Fields, initialValues, schema } from './types';

export { ProvisionQRCodeModel as NewProvisionQRCode } from '@bymason/controller-api-hooks';

interface CreateQRCodeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (qrCodeData: ProvisionQRCodeModel, provisionSessionId: string) => void;
  onError?: (message: string, code?: number) => void;
}

export function CreateQRCodeModal({ onSuccess, onError, onClose, open }: CreateQRCodeModalProps) {
  const { authToken = '', customerId = '' } = useAuth();
  const [getGroups, groups, groupsError, groupsBusy] = useGetGroups(authToken);
  const [
    createProvisionSession,
    provisionSession,
    provisionSessionError,
    provisionSessionBusy,
  ] = useCreateProvisionSession(authToken);
  const [createSession, session, sessionError, sessionBusy] = useCreateScopedSession(authToken);

  const [setToastMessage, setToastSeverity] = useToast();

  const {
    control,
    formState: { isValid },
    watch,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  const handleCreateQRCode = () => {
    createProvisionSession({
      expiresAt: new Date(Date.now() + getValues().expirationDays * 24 * 60 * 60 * 1000).toISOString(),
    });
  };

  useEffect(() => {
    if (open) {
      getGroups({ limit: 1000 });
    }
  }, [getGroups, open]);

  useEffect(() => {
    if (groupsError && !groupsBusy) {
      setToastMessage(groupsError);
      setToastSeverity('error');
    }
  }, [groupsError, groupsBusy, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (provisionSession && !provisionSessionBusy) {
      createSession({
        desc: `Scoped session for provision session ${provisionSession.sessionID}`,
        scopes: ['device:provision'],
        expiresAt: provisionSession.expiresAt,
        sessionData: {
          sessionType: 'provision',
          data: {
            sessionID: provisionSession.sessionID,
          },
        },
      });
    }
  }, [createSession, provisionSession, provisionSessionBusy]);

  useEffect(() => {
    if (
      onSuccess &&
      session &&
      provisionSession &&
      !provisionSessionBusy &&
      !sessionBusy &&
      groups &&
      groups.data
    ) {
      const formValues = getValues();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const group = groups.data.find((g) => g.id === formValues.group)!; // bad idea? We know that group exists because the form was able to submit.
      onSuccess(
        {
          customerId: customerId,
          session: session.secret,
          group: group,
          wifi: formValues.wifiLogin,
          pw: formValues.wifiPassword,
          tz: formValues.timeZone,
          type: formValues.wifiPassword ? formValues.wifiAuthType : '',
          expirationDate: provisionSession.expiresAt,
        },
        provisionSession.sessionID
      );
      onClose();
    }
  }, [
    customerId,
    getValues,
    groups,
    onSuccess,
    onClose,
    provisionSession,
    provisionSessionBusy,
    session,
    sessionBusy,
  ]);

  useEffect(() => {
    if (onError && (sessionError || provisionSessionError)) {
      onError(sessionError || provisionSessionError || '');
    }
  }, [onError, provisionSessionError, sessionError, setToastMessage, setToastSeverity]);

  return (
    <ControllerModal onClose={onClose} open={open} title='Create QR code' data-modal-name='qr-code'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <ControllerAlert sx={{ width: '100%' }} variant='outlined' severity='warning'>
            Not supported on{' '}
            <Link
              href='https://docs.bymason.com/mason-os/release-notes/os2'
              target='_blank'
              rel='noopener noreferrer'
              sx={{ color: 'currentcolor' }}
            >
              Mason OS 2.x
            </Link>
          </ControllerAlert>
          <Typography> Create a QR code to provision devices with the correct information.</Typography>
          {groups != null && (
            <ControllerValidatedSelect name={Fields.Group} fullWidth label='Group' required control={control}>
              {groups.data?.map((g) => (
                <MenuItem key={g.name} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </ControllerValidatedSelect>
          )}
          <TimeZoneSelect control={control} />
          <ControllerValidatedTextField
            label='Expiration days'
            name={Fields.ExpirationDays}
            control={control}
            fullWidth
            required
          />
          <ControllerValidatedTextField
            label='Wi-Fi login'
            name={Fields.WifiLogin}
            control={control}
            fullWidth
            inputProps={{ autoComplete: 'off' }}
          />
          <ControllerValidatedPasswordField
            label='Wi-Fi Password'
            name={Fields.WifiPassword}
            control={control}
            fullWidth
            type='password'
            inputProps={{ autoComplete: 'off' }}
          />
          <ControllerValidatedSelect
            name={Fields.WifiAuthType}
            fullWidth
            label='Wi-Fi auth type'
            required={!!watch(Fields.WifiPassword)}
            control={control}
            disabled={!watch(Fields.WifiPassword)}
          >
            <MenuItem key='WPA' value='WPA'>
              WPA
            </MenuItem>
            <MenuItem key='WPA2' value='WPA2'>
              WPA2
            </MenuItem>
            <MenuItem key='WEP' value='WEP'>
              WEP
            </MenuItem>
          </ControllerValidatedSelect>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          size='medium'
          variant='contained'
          onClick={handleCreateQRCode}
          disabled={!isValid}
        >
          Create
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
